import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// ─── GEDEELDE OFFERTE FORM URL ───────────────────────────────────────────────
// Dit is de enige "Offerte aanvragen" form — gebruik deze overal waar offerte
// wordt aangevraagd (homepage CTASection, /offerte pagina, etc.)
export const OFFERTE_FORM_URL = 'https://readdy.ai/api/form/d726ireuimmp87kg1v50';

const WHATSAPP_URL = 'https://wa.me/31624359162?text=Hi%20Shubam%2C%20ik%20heb%20zojuist%20een%20offerte%20aanvraag%20ingediend%20en%20wil%20graag%20meer%20informatie%20over%20inkopen%20uit%20China.';

const stap1Velden = [
  { name: 'naam', label: 'Naam', type: 'text', placeholder: 'Jan Jansen', required: true },
  { name: 'bedrijfsnaam', label: 'Bedrijfsnaam', type: 'text', placeholder: 'Jouw Bedrijf BV', required: false },
  { name: 'telefoon', label: 'Telefoon', type: 'tel', placeholder: '+31 6 12345678', required: false },
  { name: 'email', label: 'E-mailadres', type: 'email', placeholder: 'jou@bedrijf.nl', required: true },
];

const budgetOpties = ['Minder dan €500', '€500 – €1.000', '€1.000 – €5.000', '€5.000 – €10.000', '€10.000+'];

const volgendeStappen = [
  {
    icon: 'ri-mail-check-line',
    kleur: 'bg-[#3B9EE8]/15',
    iconKleur: 'text-[#3B9EE8]',
    titel: 'Bevestiging per e-mail',
    omschrijving: 'Je ontvangt direct een bevestiging van je aanvraag in je inbox.',
    tijd: 'Binnen enkele minuten',
  },
  {
    icon: 'ri-search-eye-line',
    kleur: 'bg-[#F7C948]/15',
    iconKleur: 'text-[#D4A800]',
    titel: 'Wij zoeken de beste fabrieken',
    omschrijving: 'Ons team gaat direct aan de slag — we benaderen fabrieken in ons netwerk voor de beste prijs.',
    tijd: 'Vandaag nog',
  },
  {
    icon: 'ri-file-list-3-line',
    kleur: 'bg-emerald-500/10',
    iconKleur: 'text-emerald-600',
    titel: 'Prijsindicatie in je mailbox',
    omschrijving: 'Je ontvangt een helder voorstel met prijs, levertijd en all-in kosten — geen verborgen bedragen.',
    tijd: 'Binnen 24 uur',
  },
];

interface ThankYouBlockProps {
  naam: string;
}

function ThankYouBlock({ naam }: ThankYouBlockProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col"
    >
      <div className="flex flex-col items-center text-center mb-7">
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.15 }}
          className="w-16 h-16 flex items-center justify-center rounded-full bg-[#3B9EE8]/12 border-2 border-[#3B9EE8]/30 mb-4"
        >
          <i className="ri-checkbox-circle-fill text-[#3B9EE8] text-3xl" />
        </motion.div>
        <motion.h3
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28 }}
          className="font-heading font-800 text-[#0A1628] text-xl mb-1.5"
        >
          {naam ? `Bedankt, ${naam.split(' ')[0]}!` : 'Aanvraag ontvangen!'}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.36 }}
          className="text-[#0A1628]/55 text-sm leading-relaxed max-w-xs"
        >
          Je aanvraag is bij ons binnengekomen. Hier is wat er nu gebeurt:
        </motion.p>
      </div>

      <div className="flex flex-col gap-3 mb-6">
        {volgendeStappen.map((stap, i) => (
          <motion.div
            key={stap.titel}
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.42 + i * 0.1 }}
            className="flex items-start gap-3 p-3.5 rounded-xl bg-[#F7F8FA] border border-[#0A1628]/5"
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${stap.kleur}`}>
              <i className={`${stap.icon} text-base ${stap.iconKleur}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <p className="font-600 text-[#0A1628] text-xs leading-snug">{stap.titel}</p>
                <span className="text-[#3B9EE8] text-xs font-500 whitespace-nowrap flex-shrink-0">{stap.tijd}</span>
              </div>
              <p className="text-[#0A1628]/50 text-xs leading-snug">{stap.omschrijving}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.a
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.72 }}
        href={WHATSAPP_URL}
        target="_blank"
        rel="nofollow noreferrer"
        className="flex items-center gap-3 p-4 rounded-xl border border-[#25D366]/30 bg-[#25D366]/6 hover:bg-[#25D366]/12 transition-all cursor-pointer group mb-4"
      >
        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#25D366]/20 flex-shrink-0">
          <i className="ri-whatsapp-line text-[#25D366] text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-600 text-[#0A1628] text-sm">Liever direct contact?</p>
          <p className="text-[#0A1628]/45 text-xs">WhatsApp Shubam — hij reageert razendsnel</p>
        </div>
        <i className="ri-arrow-right-line text-[#0A1628]/30 group-hover:text-[#25D366] transition-colors flex-shrink-0" />
      </motion.a>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.85 }}
        className="flex flex-wrap gap-2 justify-center"
      >
        <Link to="/diensten" className="inline-flex items-center gap-1.5 text-xs text-[#0A1628]/50 hover:text-[#3B9EE8] transition-colors cursor-pointer">
          <i className="ri-briefcase-4-line text-xs" />
          Onze diensten bekijken
        </Link>
        <span className="text-[#0A1628]/20 text-xs">·</span>
        <Link to="/hoe-het-werkt" className="inline-flex items-center gap-1.5 text-xs text-[#0A1628]/50 hover:text-[#3B9EE8] transition-colors cursor-pointer">
          <i className="ri-route-line text-xs" />
          Hoe het werkt
        </Link>
        <span className="text-[#0A1628]/20 text-xs">·</span>
        <Link to="/faq" className="inline-flex items-center gap-1.5 text-xs text-[#0A1628]/50 hover:text-[#3B9EE8] transition-colors cursor-pointer">
          <i className="ri-question-answer-line text-xs" />
          Veelgestelde vragen
        </Link>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.95 }}
        className="text-center text-[#0A1628]/25 text-xs mt-4 pt-4 border-t border-[#0A1628]/6"
      >
        Geen spam &bull; 100% gratis &bull; Vrijblijvend &bull; Je kunt altijd annuleren
      </motion.p>
    </motion.div>
  );
}

interface OfferteFormProps {
  /** Optionele prefill-data vanuit de calculator */
  prefill?: {
    productNaam?: string;
    aantalStuds?: string;
    totaalprijs?: string;
  };
}

export default function OfferteForm({ prefill }: OfferteFormProps) {
  const [stap, setStap] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    if (prefill?.productNaam) init['product_omschrijving'] = prefill.productNaam;
    if (prefill?.aantalStuds) init['aantal_moq'] = `${prefill.aantalStuds} stuks`;
    if (prefill?.totaalprijs) init['huidige_prijs'] = prefill.totaalprijs;
    return init;
  });

  const totalStappen = 3;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleVolgend = (e: React.FormEvent) => {
    e.preventDefault();
    setStap((s) => Math.min(s + 1, totalStappen));
  };

  const handleVorig = () => setStap((s) => Math.max(s - 1, 1));

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const params = new URLSearchParams(formData);
    try {
      const res = await fetch(OFFERTE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Er ging iets mis. Probeer het opnieuw.');
      }
    } catch {
      setError('Netwerkfout. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <ThankYouBlock naam={formData['naam'] || ''} />;
  }

  return (
    <>
      {/* Stappen indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2">
              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-700 transition-all duration-300 ${
                n <= stap ? 'bg-[#3B9EE8] text-white' : 'bg-[#0A1628]/10 text-[#0A1628]/40'
              }`}>
                {n < stap ? <i className="ri-check-line text-sm" /> : n}
              </div>
              {n < 3 && (
                <div className="flex-1 mx-2">
                  <div className="h-0.5 w-16 md:w-24 bg-[#0A1628]/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#3B9EE8] rounded-full transition-all duration-500"
                      style={{ width: n < stap ? '100%' : '0%' }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 text-xs text-[#0A1628]/40 mt-1">
          <span className="text-left">Contactgegevens</span>
          <span className="text-center">Je product</span>
          <span className="text-right">Budget &amp; timing</span>
        </div>
      </div>

      <form
        data-readdy-form
        id="offerte-aanvragen-form"
        onSubmit={stap < 3 ? handleVolgend : handleSubmit}
        className="flex flex-col gap-4"
      >
        <AnimatePresence mode="wait">
          {stap === 1 && (
            <motion.div key="stap1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="flex flex-col gap-4">
              {stap1Velden.map((veld) => (
                <div key={veld.name}>
                  <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">
                    {veld.label} {veld.required && <span className="text-[#3B9EE8]">*</span>}
                  </label>
                  <input
                    type={veld.type}
                    name={veld.name}
                    required={veld.required}
                    placeholder={veld.placeholder}
                    value={formData[veld.name] || ''}
                    onChange={handleChange}
                    className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] transition-colors"
                  />
                </div>
              ))}
            </motion.div>
          )}
          {stap === 2 && (
            <motion.div key="stap2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="flex flex-col gap-4">
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">
                  Wat wil je inkopen? <span className="text-[#3B9EE8]">*</span>
                </label>
                <textarea name="product_omschrijving" required rows={3} maxLength={500} placeholder="Omschrijf het product..." value={formData['product_omschrijving'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] transition-colors resize-none" />
              </div>
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">Bestaand product of maatwerk?</label>
                <select name="product_type" value={formData['product_type'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#3B9EE8] transition-colors bg-white">
                  <option value="">Selecteer...</option>
                  <option value="bestaand">Bestaand product</option>
                  <option value="maatwerk">Maatwerk / custom</option>
                  <option value="beide">Beide mogelijk</option>
                </select>
              </div>
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">Branding nodig?</label>
                <select name="branding" value={formData['branding'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#3B9EE8] transition-colors bg-white">
                  <option value="">Selecteer...</option>
                  <option value="ja_logo">Ja, logo / bedrukking</option>
                  <option value="ja_volledig">Ja, volledig private label</option>
                  <option value="nee">Nee, blanco product</option>
                </select>
              </div>
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">Gewenst aantal (MOQ)</label>
                <input type="text" name="aantal_moq" placeholder="bijv. 500 stuks" value={formData['aantal_moq'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] transition-colors" />
              </div>
            </motion.div>
          )}
          {stap === 3 && (
            <motion.div key="stap3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }} className="flex flex-col gap-4">
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">Richtprijs (per stuk, indien bekend)</label>
                <input type="text" name="richtprijs" placeholder="bijv. €0,50 per stuk" value={formData['richtprijs'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] transition-colors" />
              </div>
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">Huidige prijs leverancier</label>
                <input type="text" name="huidige_prijs" placeholder="Wat betaal je nu per stuk?" value={formData['huidige_prijs'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] placeholder-[#0A1628]/30 focus:outline-none focus:border-[#3B9EE8] transition-colors" />
              </div>
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">Budget <span className="text-[#3B9EE8]">*</span></label>
                <select name="budget" required value={formData['budget'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#3B9EE8] transition-colors bg-white">
                  <option value="">Selecteer budget...</option>
                  {budgetOpties.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">Wanneer nodig?</label>
                <select name="wanneer_nodig" value={formData['wanneer_nodig'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#3B9EE8] transition-colors bg-white">
                  <option value="">Selecteer...</option>
                  <option value="zo_snel_mogelijk">Zo snel mogelijk</option>
                  <option value="1_maand">Binnen 1 maand</option>
                  <option value="3_maanden">Binnen 3 maanden</option>
                  <option value="nog_geen_haast">Nog geen haast</option>
                </select>
              </div>
              <div>
                <label className="block text-[#0A1628]/70 text-xs font-600 mb-1.5 uppercase tracking-wide">Hoe heb je ons gevonden?</label>
                <select name="hoe_gevonden" value={formData['hoe_gevonden'] || ''} onChange={handleChange} className="w-full border border-[#0A1628]/15 rounded-lg px-4 py-3 text-sm text-[#0A1628] focus:outline-none focus:border-[#3B9EE8] transition-colors bg-white">
                  <option value="">Selecteer...</option>
                  <option value="google">Google</option>
                  <option value="social_media">Social media</option>
                  <option value="mond_tot_mond">Via kennissen / mond-tot-mond</option>
                  <option value="anders">Anders</option>
                </select>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {error && <p className="text-red-500 text-xs">{error}</p>}

        <div className="flex gap-3 mt-2">
          {stap > 1 && (
            <button type="button" onClick={handleVorig} className="flex-1 border border-[#0A1628]/15 hover:border-[#3B9EE8]/40 text-[#0A1628]/60 hover:text-[#0A1628] font-600 text-sm py-3.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap">
              ← Terug
            </button>
          )}
          <button type="submit" disabled={submitting} className="flex-1 bg-[#3B9EE8] hover:bg-[#2D85C5] disabled:opacity-70 text-white font-700 text-sm py-3.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap">
            {submitting ? 'Versturen...' : stap < 3 ? 'Volgende stap →' : 'Verstuur aanvraag →'}
          </button>
        </div>
        <p className="text-center text-[#0A1628]/35 text-xs">Stap {stap} van {totalStappen} &bull; Gratis &amp; vrijblijvend</p>
      </form>
    </>
  );
}
